import { handlePromise } from './promise';

type TimeoutMap = Record<string, ReturnType<typeof setTimeout>>;

class FunctionUtils {
  private timeouts: TimeoutMap = {};

  // Returns a Promise that resolves after `ms` milliseconds
  async timer(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  /**
   * Delays execution of a function with a specific id to prevent it from running too frequently.
   * Useful for debouncing logic.
   * @deprecated We can add TanStack Pacer if we need advanced control over function execution.
   */
  delayFunction(
    id: string,
    callback: (...arguments_: any[]) => void,
    callbackArguments: any[] = [],
    delay = 2000,
  ): void {
    if (this.timeouts[id]) {
      clearTimeout(this.timeouts[id]);
    }

    this.timeouts[id] = setTimeout(() => {
      callback(...callbackArguments);
    }, delay);
  }

  /**
   * Repeats an async callback until it resolves successfully, or the max number of repetitions is reached.
   * Useful for retrying network requests or polling.
   * @deprecated Use react query for loading data!
   */
  async repeatAsyncFunction<T>(
    callback: () => Promise<T>,
    repetitions: number,
    msDelay: number,
  ): Promise<T> {
    for (let index = 0; index < repetitions; index++) {
      const [response, error] = await handlePromise(callback());

      if (response) {
        return response;
      }

      if (error && index === repetitions - 1) {
        throw error;
      }

      await this.timer(msDelay);
    }

    // Should never reach here unless all attempts failed
    throw null;
  }
}

const functionUtilsInstance = new FunctionUtils();

export default functionUtilsInstance;
